import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Space, Switch, Tabs, Tag, Typography, Alert } from 'antd'
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { formatDateOnly } from './ui'
import './DocCompareViewer.css'

const { Text } = Typography

type Segment = { tag: string; text: string }

type AlignedRow = {
  row_index: number
  change_id: number | null
  change_type: string
  left_page?: number | null
  right_page?: number | null
  left: { line_no: number | null; segments: Segment[] }
  right: { line_no: number | null; segments: Segment[] }
}

type ChangeItem = {
  id: number
  type: string
  row_index: number
  left_line: number | null
  right_line: number | null
  left_page?: number | null
  right_page?: number | null
  summary: string
}

type PdfHighlight = {
  x: number
  y: number
  w: number
  h: number
  type: string
  change_id?: number | null
}

type PdfPage = {
  page_no: number
  width: number
  height: number
  image_base64?: string
  image_url?: string
  ocr?: boolean
  highlights?: PdfHighlight[]
}

type TextResult = {
  mode: 'text'
  similarity_percent?: number
  diff_percent?: number
  stats?: {
    diff_count?: number
    added_lines?: number
    removed_lines?: number
    changed_lines?: number
  }
  aligned_rows?: AlignedRow[]
  changes?: ChangeItem[]
}

type PdfResult = {
  mode: 'pdf'
  similarity_percent?: number
  diff_percent?: number
  stats?: {
    diff_count?: number
    added_lines?: number
    removed_lines?: number
    changed_lines?: number
  }
  left?: { title?: string; filename?: string }
  right?: { title?: string; filename?: string }
  left_pages?: PdfPage[]
  right_pages?: PdfPage[]
  changes?: ChangeItem[]
  aligned_rows?: AlignedRow[]
  truncated?: boolean
}

type ImageResult = {
  mode: 'image'
  similarity_percent?: number
  diff_ratio_percent?: number
  diff_pixels?: number
  total_pixels?: number
  heatmap_png_base64?: string
  left_thumb_base64?: string
  right_thumb_base64?: string
}

type Props = {
  leftTitle: string
  rightTitle: string
  result: TextResult | PdfResult | ImageResult
  expiresAt?: string | null
  onBack?: () => void
  onExportDiff?: () => void
  /** compare=文档比对；desensitize=脱敏对照（精简工具栏） */
  variant?: 'compare' | 'desensitize'
  hitCount?: number
}

const CHANGE_LABEL: Record<string, string> = {
  delete: '删除',
  insert: '增加',
  replace: '修改',
}

const CHANGE_COLOR: Record<string, string> = {
  delete: 'red',
  insert: 'green',
  replace: 'orange',
}

type DiffTabKey = 'all' | 'insert' | 'replace' | 'delete'

const DIFF_TABS: { key: DiffTabKey; label: string; types: string[] | null }[] = [
  { key: 'all', label: '全部', types: null },
  { key: 'insert', label: '增加', types: ['insert'] },
  { key: 'replace', label: '修改', types: ['replace'] },
  { key: 'delete', label: '删除', types: ['delete'] },
]

/** 优先用带后缀的原始文件名展示 */
export function docDisplayLabel(title: string, filename?: string | null): string {
  const name = (filename || '').trim()
  if (name) return name
  return (title || '').trim() || '未命名'
}

function segClass(tag: string) {
  if (tag === 'delete') return 'dcv-seg-del'
  if (tag === 'insert') return 'dcv-seg-ins'
  if (tag === 'change') return 'dcv-seg-chg'
  return 'dcv-seg-eq'
}

function renderSegments(segments: Segment[]) {
  return segments.map((s, i) => (
    <span key={i} className={segClass(s.tag)}>
      {s.text || (s.tag === 'equal' ? ' ' : '')}
    </span>
  ))
}

function PdfPageColumn({
  side,
  title,
  tagLabel,
  tagColor,
  pages,
  activePages,
  activeChange,
  pageRows,
  scrollRef,
  onScroll,
}: {
  side: 'left' | 'right'
  title: string
  tagLabel: string
  tagColor: string
  pages: PdfPage[]
  activePages: Set<number>
  activeChange: number | null
  pageRows: Map<number, AlignedRow[]>
  scrollRef: React.RefObject<HTMLDivElement | null>
  onScroll: () => void
}) {
  return (
    <div className="dcv-pdf-pane">
      <div className="dcv-doc-head">
        <Tag color={tagColor}>{tagLabel}</Tag>
        <Text ellipsis>{title}</Text>
      </div>
      <div className="dcv-pdf-scroll" ref={scrollRef} onScroll={onScroll}>
        {pages.map((p) => {
          const active = activePages.has(p.page_no)
          const highlights = p.highlights || []
          const rows = pageRows.get(p.page_no) || []
          const showTextFallback = !highlights.length && rows.length > 0
          return (
            <div
              key={p.page_no}
              id={`dcv-page-${side}-${p.page_no}`}
              className={`dcv-pdf-page${active ? ' dcv-pdf-page-active' : ''}`}
            >
              <div className="dcv-pdf-page-label">第 {p.page_no} 页</div>
              <div className="dcv-pdf-page-canvas">
                <img
                  src={p.image_url || (p.image_base64 ? `data:image/png;base64,${p.image_base64}` : '')}
                  alt={`${title} 第${p.page_no}页`}
                  loading="lazy"
                />
                {highlights.length ? (
                  <div className="dcv-pdf-overlay" aria-hidden>
                    {highlights.map((h, i) => (
                      <div
                        key={i}
                        className={`dcv-pdf-hl dcv-pdf-hl--${h.type}${h.change_id === activeChange ? ' dcv-pdf-hl-active' : ''}`}
                        style={{
                          left: `${h.x * 100}%`,
                          top: `${h.y * 100}%`,
                          width: `${h.w * 100}%`,
                          height: `${h.h * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
              {showTextFallback ? (
                <div className="dcv-pdf-page-text">
                  {rows.map((row) => (
                    <div
                      key={row.row_index}
                      className={`dcv-pdf-text-line dcv-line-${row.change_type}${
                        row.change_id === activeChange ? ' dcv-line-active' : ''
                      }`}
                    >
                      {renderSegments((side === 'left' ? row.left : row.right).segments)}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function PdfDiffPanel({
  changes,
  activeChange,
  onSelect,
  diffTab,
  onTabChange,
  diffCount,
}: {
  changes: ChangeItem[]
  activeChange: number | null
  onSelect: (c: ChangeItem) => void
  diffTab: DiffTabKey
  onTabChange: (k: DiffTabKey) => void
  diffCount?: number
}) {
  const tabItems = DIFF_TABS.map((t) => {
    const list =
      t.types === null ? changes : changes.filter((c) => t.types!.includes(c.type))
    return {
      key: t.key,
      label: `${t.label} (${list.length})`,
      children: (
        <div className="dcv-changes-list">
          {list.length ? (
            list.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`dcv-change-item${activeChange === c.id ? ' dcv-change-item-active' : ''}`}
                onClick={() => onSelect(c)}
              >
                <Space size={4} wrap>
                  <Tag color={CHANGE_COLOR[c.type] || 'default'} style={{ margin: 0 }}>
                    {CHANGE_LABEL[c.type] || c.type}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    #{c.id}
                    {c.left_page ? ` 基准P${c.left_page}` : ''}
                    {c.right_page ? ` 比对P${c.right_page}` : ''}
                  </Text>
                </Space>
                <Text className="dcv-change-summary">{c.summary}</Text>
              </button>
            ))
          ) : (
            <Text type="secondary">该分类下无差异项</Text>
          )}
        </div>
      ),
    }
  })

  return (
    <div className="dcv-changes dcv-changes--tabs">
      <div className="dcv-changes-head">
        <Text strong>比对信息</Text>
        <div className="dcv-changes-stats">
          <Text type="secondary">{diffCount ?? 0} 处差异</Text>
        </div>
      </div>
      <Tabs
        size="small"
        activeKey={diffTab}
        onChange={(k) => onTabChange(k as DiffTabKey)}
        items={tabItems}
        className="dcv-diff-tabs"
      />
    </div>
  )
}

function PdfCompareBody({
  leftTitle,
  rightTitle,
  result,
  syncScroll,
  onSyncScrollChange,
  onExportDiff,
  expiresAt,
}: {
  leftTitle: string
  rightTitle: string
  result: PdfResult
  syncScroll: boolean
  onSyncScrollChange: (v: boolean) => void
  onExportDiff?: () => void
  expiresAt?: string | null
}) {
  const leftScrollRef = useRef<HTMLDivElement>(null)
  const rightScrollRef = useRef<HTMLDivElement>(null)
  const syncing = useRef(false)
  const [activeChange, setActiveChange] = useState<number | null>(null)
  const [diffTab, setDiffTab] = useState<DiffTabKey>('all')

  const leftPages = result.left_pages || []
  const rightPages = result.right_pages || []
  const changes = result.changes || []
  const alignedRows = result.aligned_rows || []
  const leftDocLabel = docDisplayLabel(leftTitle, result.left?.filename)
  const rightDocLabel = docDisplayLabel(rightTitle, result.right?.filename)

  const leftPageRows = useMemo(() => {
    const map = new Map<number, AlignedRow[]>()
    for (const row of alignedRows) {
      if (row.change_type === 'equal') continue
      const pageNo = row.left_page
      if (!pageNo) continue
      const list = map.get(pageNo) || []
      list.push(row)
      map.set(pageNo, list)
    }
    return map
  }, [alignedRows])

  const rightPageRows = useMemo(() => {
    const map = new Map<number, AlignedRow[]>()
    for (const row of alignedRows) {
      if (row.change_type === 'equal') continue
      const pageNo = row.right_page
      if (!pageNo) continue
      const list = map.get(pageNo) || []
      list.push(row)
      map.set(pageNo, list)
    }
    return map
  }, [alignedRows])

  const activeChangeItem = changes.find((c) => c.id === activeChange) ?? null
  const activeLeftPages = useMemo(() => {
    const s = new Set<number>()
    if (activeChangeItem?.left_page) s.add(activeChangeItem.left_page)
    return s
  }, [activeChangeItem])
  const activeRightPages = useMemo(() => {
    const s = new Set<number>()
    if (activeChangeItem?.right_page) s.add(activeChangeItem.right_page)
    return s
  }, [activeChangeItem])

  const syncFrom = useCallback(
    (source: 'left' | 'right') => {
      if (!syncScroll) return
      const a = leftScrollRef.current
      const b = rightScrollRef.current
      if (!a || !b) return
      syncing.current = true
      if (source === 'left') b.scrollTop = a.scrollTop
      else a.scrollTop = b.scrollTop
      requestAnimationFrame(() => {
        syncing.current = false
      })
    },
    [syncScroll],
  )

  const scrollToChange = useCallback(
    (c: ChangeItem) => {
      const scrollSide = (side: 'left' | 'right') => {
        const pageNo = side === 'left' ? c.left_page : c.right_page
        if (!pageNo) return
        const container = side === 'left' ? leftScrollRef.current : rightScrollRef.current
        const pageEl = document.getElementById(`dcv-page-${side}-${pageNo}`)
        if (!container || !pageEl) return
        const pages = side === 'left' ? leftPages : rightPages
        const page = pages.find((p) => p.page_no === pageNo)
        const hl = page?.highlights?.find((h) => h.change_id === c.id)
        const containerRect = container.getBoundingClientRect()
        const pageRect = pageEl.getBoundingClientRect()
        let top = pageRect.top - containerRect.top + container.scrollTop
        if (hl) {
          const canvas = pageEl.querySelector('.dcv-pdf-page-canvas') as HTMLElement | null
          const canvasH = canvas?.offsetHeight ?? pageEl.offsetHeight
          top += hl.y * canvasH - container.clientHeight * 0.35
        } else {
          top += pageEl.offsetHeight / 2 - container.clientHeight / 2
        }
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      }
      scrollSide('left')
      scrollSide('right')
    },
    [leftPages, rightPages],
  )

  const selectChange = useCallback(
    (c: ChangeItem) => {
      setActiveChange(c.id)
      scrollToChange(c)
    },
    [scrollToChange],
  )

  const jumpChange = useCallback(
    (dir: 1 | -1) => {
      const tab = DIFF_TABS.find((t) => t.key === diffTab)
      const pool = tab?.types
        ? changes.filter((c) => tab.types!.includes(c.type))
        : changes
      if (!pool.length) return
      const ids = pool.map((c) => c.id)
      const cur = activeChange && ids.includes(activeChange) ? activeChange : ids[0]
      const idx = ids.indexOf(cur)
      const next = ids[(idx + dir + ids.length) % ids.length]
      const item = pool.find((c) => c.id === next)
      if (item) selectChange(item)
    },
    [activeChange, changes, diffTab, selectChange],
  )

  useEffect(() => {
    if (changes.length && activeChange === null) {
      setActiveChange(changes[0].id)
    }
  }, [changes, activeChange])

  const st = result.stats || {}
  const lowSimilarity = (result.similarity_percent ?? 100) < 10

  return (
    <>
      {lowSimilarity ? (
        <Alert
          type="warning"
          showIcon
          banner
          message="两文档内容差异极大，可能不是同一文档的不同版本；可在右侧列表逐条查看差异。"
        />
      ) : null}
      <div className="dcv-toolbar">
        <Space wrap size="middle" className="dcv-toolbar-left">
          <div className="dcv-metric">
            <Text type="secondary">差异处</Text>
            <Text strong>{st.diff_count ?? 0}</Text>
          </div>
          {result.truncated ? (
            <Tag color="warning">正文过长，部分未参与差异分析</Tag>
          ) : null}
          {expiresAt ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              保留至 {formatDateOnly(expiresAt)}
            </Text>
          ) : null}
        </Space>
        <Space wrap className="dcv-toolbar-right">
          <Switch
            checked={syncScroll}
            onChange={onSyncScrollChange}
            checkedChildren="同屏滚动"
            unCheckedChildren="独立滚动"
          />
          <Button icon={<ArrowUpOutlined />} disabled={!changes.length} onClick={() => jumpChange(-1)}>
            上一处
          </Button>
          <Button icon={<ArrowDownOutlined />} disabled={!changes.length} onClick={() => jumpChange(1)}>
            下一处
          </Button>
          {onExportDiff ? (
            <Button icon={<DownloadOutlined />} onClick={onExportDiff}>
              导出比对差异
            </Button>
          ) : null}
        </Space>
      </div>

      <div className="dcv-body dcv-body--pdf">
        <PdfPageColumn
          side="left"
          title={leftDocLabel}
          tagLabel="基准文档"
          tagColor="blue"
          pages={leftPages}
          activePages={activeLeftPages}
          activeChange={activeChange}
          pageRows={leftPageRows}
          scrollRef={leftScrollRef}
          onScroll={() => !syncing.current && syncFrom('left')}
        />
        <PdfPageColumn
          side="right"
          title={rightDocLabel}
          tagLabel="比对文档"
          tagColor="purple"
          pages={rightPages}
          activePages={activeRightPages}
          activeChange={activeChange}
          pageRows={rightPageRows}
          scrollRef={rightScrollRef}
          onScroll={() => !syncing.current && syncFrom('right')}
        />
        <PdfDiffPanel
          changes={changes}
          activeChange={activeChange}
          onSelect={selectChange}
          diffTab={diffTab}
          onTabChange={setDiffTab}
          diffCount={st.diff_count}
        />
      </div>
    </>
  )
}

function LegacyDocCompareViewer({
  leftTitle,
  rightTitle,
  result,
  expiresAt,
  onBack,
  onExportDiff,
  variant = 'compare',
  hitCount,
}: Props) {
  const isDesensitize = variant === 'desensitize'
  const isText = result.mode === 'text'
  const leftScrollRef = useRef<HTMLDivElement>(null)
  const rightScrollRef = useRef<HTMLDivElement>(null)
  const gutterScrollRef = useRef<HTMLDivElement>(null)
  const syncing = useRef(false)
  const [syncScroll, setSyncScroll] = useState(true)
  const [activeChange, setActiveChange] = useState<number | null>(null)

  const textResult = isText ? (result as TextResult) : null
  const imageResult = result.mode === 'image' ? (result as ImageResult) : null
  const changes = textResult?.changes || []
  const rows = textResult?.aligned_rows || []

  const syncFrom = useCallback(
    (source: 'left' | 'right') => {
      if (!syncScroll) return
      const a = leftScrollRef.current
      const b = rightScrollRef.current
      const g = gutterScrollRef.current
      if (!a || !b) return
      syncing.current = true
      if (source === 'left') {
        b.scrollTop = a.scrollTop
        if (g) g.scrollTop = a.scrollTop
      } else {
        a.scrollTop = b.scrollTop
        if (g) g.scrollTop = b.scrollTop
      }
      requestAnimationFrame(() => {
        syncing.current = false
      })
    },
    [syncScroll],
  )

  const scrollToRow = useCallback((rowIndex: number) => {
    document.getElementById(`dcv-row-${rowIndex}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [])

  const jumpChange = useCallback(
    (dir: 1 | -1) => {
      if (!changes.length) return
      const ids = changes.map((c) => c.id)
      const cur = activeChange ?? ids[0]
      const idx = ids.indexOf(cur)
      const next = ids[(idx + dir + ids.length) % ids.length]
      setActiveChange(next)
      const item = changes.find((c) => c.id === next)
      if (item) scrollToRow(item.row_index)
    },
    [activeChange, changes, scrollToRow],
  )

  useEffect(() => {
    if (changes.length && activeChange === null) {
      setActiveChange(changes[0].id)
    }
  }, [changes, activeChange])

  const statsBar = useMemo(() => {
    if (isDesensitize && isText && textResult) {
      const st = textResult.stats || {}
      return (
        <>
          <div className="dcv-metric">
            <Text type="secondary">敏感命中</Text>
            <Text strong className="dcv-metric-val dcv-metric-warn">
              {hitCount ?? st.diff_count ?? 0} 处
            </Text>
          </div>
          <div className="dcv-metric">
            <Text type="secondary">变更行</Text>
            <Text strong>{st.changed_lines ?? 0}</Text>
          </div>
        </>
      )
    }
    if (isText && textResult) {
      const st = textResult.stats || {}
      return (
        <>
          <div className="dcv-metric">
            <Text type="secondary">差异处</Text>
            <Text strong>{st.diff_count ?? 0}</Text>
          </div>
          <div className="dcv-metric">
            <Text type="secondary">删除/增加/修改</Text>
            <Text>
              {st.removed_lines ?? 0} / {st.added_lines ?? 0} / {st.changed_lines ?? 0}
            </Text>
          </div>
        </>
      )
    }
    if (imageResult) {
      return (
        <>
          <div className="dcv-metric">
            <Text type="secondary">差异像素占比</Text>
            <Text strong className="dcv-metric-warn">
              {imageResult.diff_ratio_percent ?? 0}%
            </Text>
          </div>
        </>
      )
    }
    return null
  }, [isDesensitize, hitCount, isText, textResult, imageResult])

  return (
    <div className={`dcv-root${isDesensitize ? ' dcv-root--desensitize' : ''}`}>
      <div className="dcv-toolbar">
        <Space wrap size="middle" className="dcv-toolbar-left">
          {onBack ? (
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
              {isDesensitize ? '返回' : '重新选择'}
            </Button>
          ) : null}
          {statsBar}
          {expiresAt ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              保留至 {formatDateOnly(expiresAt)}
            </Text>
          ) : null}
        </Space>
        <Space wrap className="dcv-toolbar-right">
          {isText ? (
            <>
              <Switch
                checked={syncScroll}
                onChange={setSyncScroll}
                checkedChildren="同屏滚动"
                unCheckedChildren="独立滚动"
              />
              <Button icon={<ArrowUpOutlined />} disabled={!changes.length} onClick={() => jumpChange(-1)}>
                上一处
              </Button>
              <Button icon={<ArrowDownOutlined />} disabled={!changes.length} onClick={() => jumpChange(1)}>
                下一处
              </Button>
            </>
          ) : null}
          {!isDesensitize && onExportDiff ? (
            <Button icon={<DownloadOutlined />} onClick={onExportDiff}>
              导出比对差异
            </Button>
          ) : null}
        </Space>
      </div>

      {isText ? (
        <div className="dcv-body">
          <div className="dcv-docs">
            <div className="dcv-doc-pane">
              <div className="dcv-doc-head">
                <Tag color="blue">{isDesensitize ? '原文' : '标准文档'}</Tag>
                <Text ellipsis>{leftTitle}</Text>
              </div>
              <div
                className="dcv-doc-scroll"
                ref={leftScrollRef}
                onScroll={() => !syncing.current && syncFrom('left')}
              >
                {rows.map((row) => (
                  <div
                    key={`l-${row.row_index}`}
                    id={`dcv-row-${row.row_index}`}
                    className={`dcv-line dcv-line-${row.change_type}${
                      row.change_id === activeChange ? ' dcv-line-active' : ''
                    }`}
                  >
                    <span className="dcv-ln">{row.left.line_no ?? ''}</span>
                    <span className="dcv-text">{renderSegments(row.left.segments)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="dcv-gutter" aria-hidden>
              <div className="dcv-gutter-scroll" ref={gutterScrollRef}>
                {rows.map((row) => (
                  <div key={`g-${row.row_index}`} className="dcv-gutter-line">
                    {row.change_type !== 'equal' ? (
                      <span
                        className={`dcv-gutter-mark dcv-gutter-mark-${row.change_type}${
                          row.change_id === activeChange ? ' dcv-gutter-mark-active' : ''
                        }`}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="dcv-doc-pane">
              <div className="dcv-doc-head">
                <Tag color={isDesensitize ? 'green' : 'purple'}>{isDesensitize ? '脱敏后' : '比对文档'}</Tag>
                <Text ellipsis>{rightTitle}</Text>
              </div>
              <div
                className="dcv-doc-scroll"
                ref={rightScrollRef}
                onScroll={() => !syncing.current && syncFrom('right')}
              >
                {rows.map((row) => (
                  <div
                    key={`r-${row.row_index}`}
                    className={`dcv-line dcv-line-${row.change_type}${
                      row.change_id === activeChange ? ' dcv-line-active' : ''
                    }`}
                  >
                    <span className="dcv-ln">{row.right.line_no ?? ''}</span>
                    <span className="dcv-text">{renderSegments(row.right.segments)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="dcv-changes">
            <Text strong className="dcv-changes-title">
              {isDesensitize ? `脱敏变更 (${changes.length})` : `差异列表 (${changes.length})`}
            </Text>
            <div className="dcv-changes-list">
              {changes.length ? (
                changes.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`dcv-change-item${activeChange === c.id ? ' dcv-change-item-active' : ''}`}
                    onClick={() => {
                      setActiveChange(c.id)
                      scrollToRow(c.row_index)
                    }}
                  >
                    <Space size={4} wrap>
                      <Tag color={CHANGE_COLOR[c.type] || 'default'} style={{ margin: 0 }}>
                        {CHANGE_LABEL[c.type] || c.type}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        #{c.id}
                        {c.left_line ? ` 左${c.left_line}行` : ''}
                        {c.right_line ? ` 右${c.right_line}行` : ''}
                      </Text>
                    </Space>
                    <Text className="dcv-change-summary">{c.summary}</Text>
                  </button>
                ))
              ) : (
                <Text type="secondary">两篇文档完全一致</Text>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="dcv-image-body">
          <div className="dcv-image-pane">
            <Text strong>标准图</Text>
            {imageResult?.left_thumb_base64 ? (
              <img src={`data:image/png;base64,${imageResult.left_thumb_base64}`} alt="左" />
            ) : null}
          </div>
          <div className="dcv-image-pane">
            <Text strong>比对图</Text>
            {imageResult?.right_thumb_base64 ? (
              <img src={`data:image/png;base64,${imageResult.right_thumb_base64}`} alt="右" />
            ) : null}
          </div>
          <div className="dcv-image-pane">
            <Text strong>差异热力图</Text>
            {imageResult?.heatmap_png_base64 ? (
              <img src={`data:image/png;base64,${imageResult.heatmap_png_base64}`} alt="热力图" />
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

export function DocCompareViewer(props: Props) {
  if (props.result.mode === 'pdf' && props.variant !== 'desensitize') {
    return <PdfCompareViewerRoot {...props} />
  }
  return <LegacyDocCompareViewer {...props} />
}

function PdfCompareViewerRoot({
  leftTitle,
  rightTitle,
  result,
  expiresAt,
  onExportDiff,
}: Props) {
  const [syncScroll, setSyncScroll] = useState(true)
  return (
    <div className="dcv-root">
      <PdfCompareBody
        leftTitle={leftTitle}
        rightTitle={rightTitle}
        result={result as PdfResult}
        syncScroll={syncScroll}
        onSyncScrollChange={setSyncScroll}
        onExportDiff={onExportDiff}
        expiresAt={expiresAt}
      />
    </div>
  )
}
